import { useMemo, useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSpaceStore } from '@/stores/spaceStore'
import { useCalendarStore } from '@/stores/calendarStore'
import { useTagsStore } from '@/stores/tagsStore'
import { useEventStore } from '@/stores/eventStore'
import { api } from '@/api/client'
import type { Event } from '@/types/models'
import styles from './BottomNav.module.css'

function getNextOccurrence(evt: Event): Date | null {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const [, m, d] = evt.date.split('-').map(Number)
  if (evt.repeat === 'yearly') {
    const next = new Date(today.getFullYear(), m - 1, d)
    if (next < today) next.setFullYear(next.getFullYear() + 1)
    return next
  }
  if (evt.repeat === 'monthly') {
    const next = new Date(today.getFullYear(), today.getMonth(), d)
    if (next < today) next.setMonth(next.getMonth() + 1)
    return next
  }
  if (evt.repeat === 'weekly') {
    const eventDate = new Date(evt.date)
    eventDate.setHours(0, 0, 0, 0)
    const diff = eventDate.getDay() - today.getDay()
    const next = new Date(today)
    if (diff === 0) return next // 今天就是重复日
    next.setDate(next.getDate() + ((diff + 7) % 7))
    return next
  }
  if (evt.repeat === 'periodic' && evt.periodDays && evt.periodDays > 0) {
    const eventDate = new Date(evt.date)
    eventDate.setHours(0, 0, 0, 0)
    const diff = today.getTime() - eventDate.getTime()
    const daysSince = Math.floor(diff / 86400000)
    const remaining = daysSince % evt.periodDays
    const next = new Date(today)
    next.setDate(next.getDate() + (remaining === 0 ? 0 : evt.periodDays - remaining))
    if (next < today) next.setDate(next.getDate() + evt.periodDays)
    return next
  }
  const eventDate = new Date(evt.date)
  eventDate.setHours(0, 0, 0, 0)
  if (eventDate < today) return null
  return eventDate
}

const tabs = [
  { path: '/', icon: '📅', label: '日历', key: 'calendar' },
  { path: '/data', icon: '📋', label: '数据', key: 'data' },
  { path: '/messages', icon: '💬', label: '消息', key: 'messages' },
  { path: '/events', icon: '🔔', label: '事件', key: 'events' },
  { path: '/profile', icon: '👤', label: '我的', key: 'profile' },
]

export default function BottomNav() {
  const navigate = useNavigate()
  const location = useLocation()
  const { activeSpace } = useSpaceStore()
  const switchSpace = useSpaceStore(s => s.switchSpace)
  const loadMonth = useCalendarStore(s => s.loadMonth)
  const loadTags = useTagsStore(s => s.loadTags)
  const events = useEventStore(s => s.events)
  const [messageCount, setMessageCount] = useState(0)

  const checkMessages = useCallback(async () => {
    try {
      const res = await api.get<{ unread: number; hasPendingCouple: boolean; hasPendingSpace: boolean; hasPendingFriend: boolean }>('/api/notifications/unread-count')
      setMessageCount(res.unread + (res.hasPendingCouple ? 1 : 0) + (res.hasPendingSpace ? 1 : 0) + (res.hasPendingFriend ? 1 : 0))
    } catch {}
  }, [])

  useEffect(() => {
    checkMessages()
    const interval = setInterval(checkMessages, 30000)
    return () => clearInterval(interval)
  }, [checkMessages])

  const urgentCount = useMemo(() => {
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    const limit = new Date(now)
    limit.setDate(limit.getDate() + 7)
    return events.filter(e => {
      const next = getNextOccurrence(e)
      return next && next >= now && next <= limit
    }).length
  }, [events])

  const getKey = () => {
    const p = location.pathname
    if (p.startsWith('/vault')) return 'vault'
    if (p.startsWith('/period')) return 'period'
    if (p.startsWith('/data') || p.startsWith('/stats') || p.startsWith('/timeline')) return 'data'
    if (p.startsWith('/messages') || p.startsWith('/notifications') || p.startsWith('/friends')) return 'messages'
    if (p.startsWith('/events')) return 'events'
    if (p.startsWith('/profile')) return 'profile'
    return 'calendar'
  }
  const active = getKey()

  const handleTabClick = (tab: typeof tabs[number]) => {
    const fromVault = activeSpace === 'vault' || active === 'vault'

    // 从 vault 切到日历或数据页时，恢复到之前的主空间
    if (fromVault && (tab.key === 'calendar' || tab.key === 'data')) {
      const { lastMainSpace } = useSpaceStore.getState()
      const target = (lastMainSpace === 'vault' || lastMainSpace === 'period') ? 'default' : lastMainSpace
      switchSpace(target)
      setTimeout(() => { loadTags(); loadMonth() }, 50)
    }

    navigate(tab.path)
  }

  return (
    <nav className={styles.nav}>
      {tabs.map(tab => (
        <button
          key={tab.key}
          className={`${styles.item} ${active === tab.key ? styles.active : ''}`}
          onClick={() => handleTabClick(tab)}
        >
          <span className={styles.icon}>
            {tab.icon}
            {tab.key === 'events' && urgentCount > 0 && <span className={styles.badge}>{urgentCount}</span>}
            {tab.key === 'messages' && messageCount > 0 && <span className={styles.badge}>{messageCount > 9 ? '9+' : messageCount}</span>}
          </span>
          <span className={styles.label}>{tab.label}</span>
        </button>
      ))}
    </nav>
  )
}
